import { useEffect } from "react";
import type { IntakeValues } from "@/api/ticket-intake";
import { AttachmentScanStatus, type TicketDto } from "@/api/tickets";
import { Permissions } from "@/auth/permissions";
import { usePermission } from "@/auth/session-provider";
import { Select, SelectContent, SelectGroup, SelectItem, SelectTrigger, SelectValue } from "@/components/select";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Field, FieldDescription, FieldError, FieldGroup, FieldLabel } from "@/components/ui/field";
import { Spinner } from "@/components/ui/spinner";
import { cn } from "@/utils";
import { useIntakeMessages } from "./intake-messages";
import { TicketIntakeAdditionalFields } from "./ticket-intake-additional-fields";
import { TicketIntakeCustomerFields } from "./ticket-intake-customer-fields";
import { TicketIntakeSection } from "./ticket-intake-section";
import { TicketIntakeTicketFields } from "./ticket-intake-ticket-fields";
import { useTicketIntake } from "./use-ticket-intake";

type Props = {
  initialContext?: IntakeValues;
  projectCode?: string;
  layout?: "default" | "workbench";
  onCreated: (ticket: TicketDto) => void;
  onCancel?: () => void;
  onStateChange?: (state: { dirty: boolean; pending: boolean }) => void;
};

/** Shared intake behavior with stacked sections for the embedded workbench. */
export function TicketIntakeForm({ onStateChange, onCancel, layout = "default", ...props }: Props) {
  const m = useIntakeMessages();
  const { can } = usePermission();
  const form = useTicketIntake({ ...props, enabled: can(Permissions.tickets.create) });
  const { session, busy } = form;
  useEffect(() => {
    onStateChange?.({ dirty: form.dirty, pending: busy });
  }, [busy, form.dirty, onStateChange]);
  useEffect(() => {
    const warn = (event: BeforeUnloadEvent) => {
      if (form.dirty || busy) {
        event.preventDefault();
        event.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy, form.dirty]);

  if (!can(Permissions.tickets.create)) return <p className="text-sm text-muted-foreground">{m.notAllowed}</p>;
  return (
    <form
      className={cn("@container/intake flex flex-col", layout === "workbench" ? "gap-6" : "gap-8")}
      noValidate
      onSubmit={(event) => {
        event.preventDefault();
        if (!busy) void form.submit();
      }}
    >
      {!form.fixedProject ? (
        <FieldGroup className="grid sm:grid-cols-2">
          <Field data-disabled={busy || form.initializing || Boolean(session)}>
            <FieldLabel htmlFor="intake-project" data-required="true">
              {m.project}
            </FieldLabel>
            <Select disabled={busy || form.initializing || Boolean(session)} value={session?.projectId} onValueChange={(id) => void form.start(id)}>
              <SelectTrigger id="intake-project" className="w-72" loading={form.projects.isPending}>
                <SelectValue placeholder={m.chooseProject} />
              </SelectTrigger>
              <SelectContent>
                <SelectGroup>
                  {(form.projects.data ?? []).map((project) => (
                    <SelectItem key={project.id} value={project.id}>
                      {project.name} · {project.code}
                    </SelectItem>
                  ))}
                </SelectGroup>
              </SelectContent>
            </Select>
            {form.projects.isError ? (
              <FieldError>
                {m.error}
                <Button type="button" variant="link" onClick={() => void form.projects.refetch()}>
                  {m.retry}
                </Button>
              </FieldError>
            ) : null}
            {form.projects.isSuccess && form.projects.data.length === 0 ? <FieldDescription>{m.noProjects}</FieldDescription> : null}
          </Field>
        </FieldGroup>
      ) : form.projectError ? (
        <Alert variant="destructive">
          <AlertDescription>
            <p>{form.projectError}</p>
            <Button disabled={busy || form.initializing} type="button" variant="outline" onClick={() => void form.retryProject()}>
              {m.retry}
            </Button>
          </AlertDescription>
        </Alert>
      ) : null}
      {form.error && !(form.fixedProject && !session) ? (
        <Alert variant="destructive">
          <AlertDescription>
            <p>{form.error}</p>
            {form.versionConflict ? (
              <>
                <p>{m.versionConflict}</p>
                <Button disabled={busy} type="button" variant="outline" onClick={() => void form.reloadForm()}>
                  {m.reloadForm}
                </Button>
              </>
            ) : null}
          </AlertDescription>
        </Alert>
      ) : null}
      {!session && layout !== "workbench" ? (
        form.projectError ? null : (
          <div role="status" className="flex items-center gap-2 py-6 text-sm text-muted-foreground">
            {form.initializing || busy ? <Spinner /> : null}
            {form.initializing || busy ? m.loading : m.start}
          </div>
        )
      ) : (
        <>
          <div className="flex min-w-0 flex-col gap-8" aria-busy={busy}>
            {layout === "workbench" ? <TicketIntakeCustomerFields form={form} layout={layout} /> : null}
            <TicketIntakeSection title={m.ticketInformation} description={layout === "workbench" ? undefined : m.requiredHint}>
              <TicketIntakeTicketFields form={form} layout={layout} />
            </TicketIntakeSection>
            {layout === "default" ? <TicketIntakeCustomerFields form={form} /> : null}
            {layout === "default" && (form.initializing || (session?.form.schema.fields.length ?? 0) > 0) ? (
              <TicketIntakeSection title={m.business} description={m.businessHint}>
                <TicketIntakeAdditionalFields form={form} />
              </TicketIntakeSection>
            ) : null}
          </div>
          <div className="sticky bottom-0 z-10 flex shrink-0 flex-wrap items-center justify-between gap-3 bg-card py-3">
            <div className="flex items-center gap-2">
              <Button
                disabled={
                  !session ||
                  form.initializing ||
                  busy ||
                  form.versionConflict ||
                  session.attachments.some((file) => file.scanStatus !== AttachmentScanStatus.Clean)
                }
                type="submit"
              >
                {busy ? <Spinner data-icon="inline-start" /> : null}
                {busy ? m.processing : m.submit}
              </Button>
              {onCancel ? (
                <Button type="button" variant="outline" disabled={busy} onClick={onCancel}>
                  {m.cancel}
                </Button>
              ) : null}
            </div>
            {layout === "default" ? <p className="text-xs text-muted-foreground">{m.submitHint}</p> : null}
          </div>
        </>
      )}
    </form>
  );
}
